'use client';
import { Tabs, TabsList, TabsTrigger } from '@ui/components/ui/tabs';
import type {
  IMessagesQueryParams,
  TPostCommentOrdering,
} from '@ui/components/organisms/message/message.typings';
import { cn } from '@ui/lib/utils';

export const COMMENT_SORT_OPTIONS: ReadonlyArray<{ value: TPostCommentOrdering; label: string }> = [
  { value: 'trending', label: 'Trending' },
  { value: 'desc', label: 'Newest' },
];

const isCommentSortOrdering = (value: string): value is TPostCommentOrdering =>
  COMMENT_SORT_OPTIONS.some((option) => option.value === value);

/** Id of the tab for `ordering`; the comment list's tabpanel is labelled by the selected one. */
export const commentSortTabId = (panelId: string, ordering: IMessagesQueryParams['ordering']) =>
  `${panelId}-tab-${ordering ?? 'none'}`;

interface Props {
  value: IMessagesQueryParams['ordering'];
  onValueChange: (ordering: TPostCommentOrdering) => void;
  /** Id of the element holding the comment list these tabs sort. */
  panelId: string;
  className?: string;
}

/**
 * Trending | Newest above a post's comments. Activation is manual: arrow keys move focus and
 * Enter or Space picks, so moving through the tabs does not fetch each ordering.
 */
export const CommentSortTabs = ({ value, onValueChange, panelId, className }: Props) => (
  <Tabs
    value={value ?? ''}
    onValueChange={(next) => {
      if (isCommentSortOrdering(next)) onValueChange(next);
    }}
    activationMode="manual">
    <TabsList aria-label="Sort comments" className={cn('h-8 shrink-0', className)}>
      {COMMENT_SORT_OPTIONS.map((option) => (
        <TabsTrigger
          key={option.value}
          value={option.value}
          id={commentSortTabId(panelId, option.value)}
          aria-controls={panelId}
          className="font-nunito h-6 px-3 text-xs font-semibold sm:text-sm">
          {option.label}
        </TabsTrigger>
      ))}
    </TabsList>
  </Tabs>
);
